/**
 * Company Information Mapping Utilities
 *
 * ファイルアップロード結果とURL抽出結果から会社情報オブジェクトを
 * 生成・マージするためのユーティリティ関数
 */

import type { UploadData } from '../types/bcp-generation';
import type { BCPNavigationState } from '../types/navigation';

type CompanyInfo = NonNullable<BCPNavigationState['companyInfo']>;

/**
 * 重要事業テキストを正規化する
 *
 * 複数の区切り文字(改行、句点、読点、中黒、セミコロン)で区切られたテキストを
 * 改行区切りの標準形式に変換する
 *
 * @param text - 正規化する重要事業テキスト
 * @returns 改行区切りで整形されたテキスト
 */
export const normalizeCriticalBusinessText = (text: string): string => {
  if (!text || text.trim().length === 0) {
    return '';
  }

  // 複数の区切り文字を改行に統一
  // \n, 。, 、, ・, ; などで区切られたテキストを改行区切りに変換
  const normalized = text
    .split(/[\n。、・;]+/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .join('\n');

  return normalized;
};

/**
 * 全角数字を半角数字に変換する
 *
 * @param text - 変換するテキスト
 * @returns 半角数字に変換されたテキスト
 */
const toHalfWidth = (text: string): string => {
  return text.replace(/[０-９]/g, (char) => {
    return String.fromCharCode(char.charCodeAt(0) - 0xfee0);
  });
};

/**
 * 従業員数から単位を除去し、数字のみを返す
 *
 * @param employees - 従業員数(数字のみまたは単位付き)
 * @returns 数字のみの従業員数
 */
const normalizeEmployeeCount = (employees: string | number | undefined): string | undefined => {
  console.log('[normalizeEmployeeCount] Input:', employees, typeof employees);

  if (!employees) {
    console.log('[normalizeEmployeeCount] No employees, returning undefined');
    return undefined;
  }

  const employeeStr = String(employees).trim();
  console.log('[normalizeEmployeeCount] employeeStr:', employeeStr);

  if (!employeeStr) {
    console.log('[normalizeEmployeeCount] Empty string, returning undefined');
    return undefined;
  }

  // 全角数字を半角に変換
  const halfWidth = toHalfWidth(employeeStr);

  // 単位(名、人)を除去
  const numberOnly = halfWidth.replace(/[名人]/g, '').trim();
  console.log('[normalizeEmployeeCount] After removing units:', numberOnly);

  // 数字のみかチェック
  if (/^\d+$/.test(numberOnly)) {
    console.log('[normalizeEmployeeCount] Valid number, returning:', numberOnly);
    return numberOnly;
  }

  console.log('[normalizeEmployeeCount] Invalid format, returning undefined');
  return undefined;
};

/**
 * ファイルアップロード結果から会社情報オブジェクトを生成する
 *
 * merged_extracted_infoを優先的に使用し、存在しない場合は
 * extracted_infoをフォールバックとして使用する
 *
 * @param uploadData - アップロードAPIのレスポンスデータ
 * @returns 会社情報オブジェクト
 */
export const buildCompanyInfoFromUpload = (
  uploadData: UploadData | undefined
): CompanyInfo => {
  console.log('[buildCompanyInfoFromUpload] uploadData:', uploadData);

  const merged =
    uploadData?.merged_extracted_info ?? uploadData?.extracted_info ?? {};

  console.log('[buildCompanyInfoFromUpload] merged:', merged);
  console.log('[buildCompanyInfoFromUpload] merged.employees:', merged.employees);

  const employeeCount = normalizeEmployeeCount(merged.employees);
  console.log('[buildCompanyInfoFromUpload] employeeCount after normalize:', employeeCount);

  const result = {
    companyName: (merged.company_name ?? '').trim(),
    location: (merged.address ?? '').replace(/\n+$/g, ''),
    employeeCount: employeeCount,
    businessDescription: merged.business_content ?? '',
    criticalBusiness: normalizeCriticalBusinessText(
      merged.main_business ?? ''
    ),
  };

  console.log('[buildCompanyInfoFromUpload] result:', result);
  return result;
};

/**
 * URL抽出結果とファイル抽出結果をマージする
 *
 * URL抽出値を優先し、空欄のみファイル値で補完する。
 * URLスクレイパーがデータを持たない場合は空文字列を返すため、
 * truthy checkを使用してファイルから抽出された豊富な情報を保持する。
 *
 * @param urlInfo - URL抽出結果(部分的な会社情報)
 * @param uploadInfo - ファイル抽出結果(完全な会社情報)
 * @returns マージされた会社情報オブジェクト
 */
export const mergeCompanyInfo = (
  urlInfo: Partial<CompanyInfo>,
  uploadInfo: CompanyInfo
): CompanyInfo => {
  return {
    companyName:
      urlInfo.companyName?.trim() || uploadInfo.companyName || '',
    location: urlInfo.location?.trim() || uploadInfo.location || '',
    employeeCount: urlInfo.employeeCount || uploadInfo.employeeCount,
    businessDescription:
      urlInfo.businessDescription?.trim() || uploadInfo.businessDescription,
    criticalBusiness:
      urlInfo.criticalBusiness?.trim() || uploadInfo.criticalBusiness,
  };
};
